import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, MessageCircle, Send, Trash2, Dumbbell } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMOJIS = ["🔥", "❤️", "💪", "🌈", "🥚"];

function timeAgo(iso) {
  try {
    const ms = Date.now() - new Date(iso).getTime();
    const s = Math.floor(ms / 1000);
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
  } catch {
    return "";
  }
}

function fmtFeedDuration(sec) {
  if (!sec || sec < 0) return "00:00";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = Math.floor(sec % 60);
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export default function FriendsFeed() {
  const { refreshUser, user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openComments, setOpenComments] = useState({}); // event_id -> bool
  const [commentsByEvent, setCommentsByEvent] = useState({}); // event_id -> []
  const [commentLoading, setCommentLoading] = useState({});
  const [draftByEvent, setDraftByEvent] = useState({});
  const [congratsBusy, setCongratsBusy] = useState({});

  async function congratulate(event) {
    const sid = event.share_id;
    if (!sid || event.i_congratulated || congratsBusy[sid]) return;
    setCongratsBusy((m) => ({ ...m, [sid]: true }));
    try {
      const { data } = await axios.post(`${API}/cardio/congratulate/${sid}`);
      toast.success("🎉 Congrats sent!", {
        description: data?.message_text ? `"${data.message_text}"` : "Delivered as a DM.",
        duration: 5000,
      });
      // Optimistically mark as congratulated in local state
      setEvents((evts) => evts.map((x) =>
        x.share_id === sid
          ? { ...x, i_congratulated: true, congrats_count: (x.congrats_count || 0) + 1 }
          : x
      ));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send congrats.");
    } finally {
      setCongratsBusy((m) => ({ ...m, [sid]: false }));
    }
  }

  const loadComments = async (eventId) => {
    setCommentLoading((m) => ({ ...m, [eventId]: true }));
    try {
      const { data } = await axios.get(`${API}/friends/feed/comments`, {
        params: { event_id: eventId },
      });
      setCommentsByEvent((m) => ({ ...m, [eventId]: data.comments || [] }));
    } catch (e) {
      // silent
    } finally {
      setCommentLoading((m) => ({ ...m, [eventId]: false }));
    }
  };

  const toggleComments = async (eventId) => {
    const next = !openComments[eventId];
    setOpenComments((m) => ({ ...m, [eventId]: next }));
    if (next && !commentsByEvent[eventId]) await loadComments(eventId);
  };

  const postComment = async (eventId) => {
    const text = (draftByEvent[eventId] || "").trim();
    if (!text) return;
    try {
      const { data } = await axios.post(`${API}/friends/feed/comments`, {
        event_id: eventId,
        text,
      });
      setCommentsByEvent((m) => ({
        ...m,
        [eventId]: [...(m[eventId] || []), data.comment],
      }));
      setDraftByEvent((m) => ({ ...m, [eventId]: "" }));
      refreshUser?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Comment failed.");
    }
  };

  const deleteComment = async (eventId, commentId) => {
    try {
      await axios.delete(`${API}/friends/feed/comments/${commentId}`);
      setCommentsByEvent((m) => ({
        ...m,
        [eventId]: (m[eventId] || []).filter((c) => c.comment_id !== commentId),
      }));
    } catch (e) {
      toast.error("Delete failed.");
    }
  };

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/friends/feed?limit=30`);
      setEvents(data.events || []);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const react = async (event, emoji) => {
    // Optimistic
    setEvents((evts) =>
      evts.map((e) => {
        if (e.event_id !== event.event_id) return e;
        const cur = e.reactions[emoji] || { count: 0, mine: false };
        const mine = !cur.mine;
        const count = cur.count + (mine ? 1 : -1);
        return {
          ...e,
          reactions: { ...e.reactions, [emoji]: { count: Math.max(0, count), mine } },
        };
      })
    );
    try {
      await axios.post(`${API}/friends/feed/react`, {
        event_id: event.event_id,
        emoji,
      });
      refreshUser?.();
    } catch {
      // revert on failure
      load();
    }
  };

  return (
    <div
      data-testid="friends-feed"
      className="bg-[#0F0F0F] border border-zinc-800 p-6 mt-6"
    >
      <div className="flex items-end justify-between mb-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">
            Activity Feed
          </div>
          <h2 className="font-display text-3xl text-white">
            The Circle <span className="text-[#D4AF37]">Rises.</span>
          </h2>
        </div>
      </div>

      {loading ? (
        <div className="text-center text-neutral-500 py-6 text-sm">
          <Loader2 size={20} className="animate-spin inline-block mr-2" />
          Loading…
        </div>
      ) : events.length === 0 ? (
        <p className="text-sm text-neutral-500 text-center py-8">
          No activity yet. Earn a badge or add friends to see the feed light up.
        </p>
      ) : (
        <ul className="space-y-3">
          {events.map((e) => (
            <li
              key={e.event_id}
              data-testid={`feed-item-${e.badge_id || e.event_id}`}
              className={`bg-[#0A0A0A] border ${
                e.is_me ? "border-[#D4AF37]" : "border-zinc-900"
              } p-4`}
            >
              <div className="flex items-center gap-3 mb-2">
                {e.user_picture ? (
                  <img
                    src={e.user_picture}
                    alt={e.user_name}
                    className="w-10 h-10 object-contain bg-black border border-zinc-700"
                  />
                ) : (
                  <div className="w-10 h-10 bg-[#4C1D95] border border-zinc-700 flex items-center justify-center font-display text-base text-[#D4AF37]">
                    {(e.user_name || "?").charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  {e.kind === "workout_share" ? (
                    <p className="text-sm text-white leading-snug">
                      <span className="font-bold">{e.is_me ? "You" : e.user_name}</span>{" "}
                      <span className="text-neutral-400">shared a workout</span>{" "}
                      <Dumbbell size={12} className="inline -mt-1 text-[#DC2626]" />
                    </p>
                  ) : e.kind === "recipe_share" ? (
                    <p className="text-sm text-white leading-snug">
                      <span className="font-bold">{e.is_me ? "You" : e.user_name}</span>{" "}
                      <span className="text-neutral-400">shared a recipe 🍳</span>
                    </p>
                  ) : e.kind === "cardio_share" ? (
                    <p className="text-sm text-white leading-snug">
                      <span className="font-bold">{e.is_me ? "You" : e.user_name}</span>{" "}
                      <span className="text-neutral-400">finished a</span>{" "}
                      <span className="text-[#D4AF37] font-bold">
                        {(e.payload?.distance_km ?? 0).toFixed(2)} km {e.payload?.mode_label || "Run"} 🏃
                      </span>
                    </p>
                  ) : e.kind === "omni_hatch" ? (
                    <p className="text-sm text-white leading-snug">
                      <span className="font-bold">{e.is_me ? "You" : e.user_name}</span>{" "}
                      <span className="text-neutral-400">just hatched a</span>{" "}
                      <span
                        className="font-bold inline-block"
                        style={{
                          background: "linear-gradient(90deg,#FF0080,#FFD700,#00BFFF,#8A2BE2)",
                          backgroundClip: "text",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                        }}
                      >
                        SSS Omni Rainbow 🌈
                      </span>{" "}
                      <span className="text-neutral-400">egg!</span>
                    </p>
                  ) : (
                    <p className="text-sm text-white leading-snug">
                      <span className="font-bold">{e.is_me ? "You" : e.user_name}</span>{" "}
                      <span className="text-neutral-400">unlocked</span>{" "}
                      <span className="text-[#D4AF37] font-bold">
                        {e.badge_icon} {e.badge_title}
                      </span>
                    </p>
                  )}
                  <p className="text-[10px] text-neutral-500 uppercase tracking-widest mt-0.5">
                    {timeAgo(e.timestamp)}
                  </p>
                </div>
              </div>
              {(e.kind === "workout_share" || e.kind === "recipe_share") && e.payload?.title && (
                <a
                  href={e.kind === "workout_share" ? "/#workout-of-the-week" : "/#recipe-of-the-week"}
                  data-testid={`feed-share-card-${e.event_id}`}
                  className="block border-2 border-[#D4AF37] bg-black/40 p-3 mb-2 hover:bg-[#D4AF37]/5 transition-colors"
                >
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-1">
                    {e.kind === "workout_share" ? "Workout" : "Recipe"}
                  </div>
                  <div className="font-display text-lg text-white leading-tight">
                    {e.payload.title}
                  </div>
                  {e.payload.tagline && (
                    <div className="text-[11px] text-neutral-400 italic mt-1">
                      "{e.payload.tagline}"
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2 mt-2 text-[10px] uppercase tracking-widest text-neutral-500">
                    {e.payload.focus && (
                      <span className="border border-zinc-700 px-1.5 py-0.5">{e.payload.focus}</span>
                    )}
                    {e.payload.level && (
                      <span className="border border-zinc-700 px-1.5 py-0.5">{e.payload.level}</span>
                    )}
                    {e.payload.days_per_week && (
                      <span className="border border-zinc-700 px-1.5 py-0.5">{e.payload.days_per_week}d/wk</span>
                    )}
                  </div>
                </a>
              )}
              {e.kind === "omni_hatch" && e.payload && (
                <div
                  data-testid={`feed-omni-card-${e.event_id}`}
                  className="block border-2 p-3 mb-2 omni-celebration-bg relative"
                >
                  <div className="absolute inset-0 bg-black/55" />
                  <div className="relative">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-white font-bold mb-1">
                      🌈 SSS · 1 in a Million
                    </div>
                    <div className="font-display text-xl text-white leading-tight">
                      Omni Rainbow Hatched
                    </div>
                    <div className="font-mono tabular-nums text-base text-white mt-1">
                      +{e.payload.nests} 🪹
                      {e.payload.colored_feather_color && (
                        <span className="ml-3 text-[11px] uppercase tracking-widest">
                          🪶 {e.payload.colored_feather_color}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {e.kind === "cardio_share" && e.payload && (
                <div
                  data-testid={`feed-cardio-card-${e.event_id}`}
                  className="block border-2 border-[#D4AF37] bg-black/40 p-3 mb-2"
                >
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-1">
                    {e.payload.mode_label || "Run"}
                  </div>
                  <div className="grid grid-cols-3 gap-3 mb-1">
                    <div>
                      <div className="text-[9px] uppercase tracking-widest text-neutral-500">Distance</div>
                      <div className="font-mono tabular-nums text-base text-white">{(e.payload.distance_km ?? 0).toFixed(2)} km</div>
                    </div>
                    <div>
                      <div className="text-[9px] uppercase tracking-widest text-neutral-500">Time</div>
                      <div className="font-mono tabular-nums text-base text-white">{fmtFeedDuration(e.payload.duration_sec)}</div>
                    </div>
                    <div>
                      <div className="text-[9px] uppercase tracking-widest text-neutral-500">Pace</div>
                      <div className="font-mono tabular-nums text-base text-white">{e.payload.pace_label || "—"}</div>
                    </div>
                  </div>
                  {Array.isArray(e.payload.prs) && e.payload.prs.length > 0 && (
                    <div data-testid={`feed-cardio-prs-${e.event_id}`} className="mb-2 mt-1 flex flex-wrap gap-1.5">
                      {e.payload.prs.map((pr) => (
                        <span
                          key={pr.key}
                          className="inline-flex items-center text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 bg-[#D4AF37] text-black"
                        >
                          🎉 {pr.label}
                        </span>
                      ))}
                    </div>
                  )}
                  {e.payload.pt_awarded > 0 && (
                    <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-bold mb-2">
                      +{e.payload.pt_awarded} Phoenix Tokens 🐦‍🔥
                    </div>
                  )}
                  {!e.is_me && (
                    <button
                      data-testid={`congratulate-${e.share_id}`}
                      onClick={() => congratulate(e)}
                      disabled={e.i_congratulated || congratsBusy[e.share_id]}
                      className={`w-full mt-2 text-xs uppercase tracking-widest font-bold py-2 px-3 border transition-colors ${
                        e.i_congratulated
                          ? "bg-[#D4AF37]/10 border-[#D4AF37] text-[#D4AF37] cursor-default"
                          : "bg-transparent border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black"
                      }`}
                    >
                      {e.i_congratulated
                        ? `🎉 Congratulated${e.congrats_count > 1 ? ` · ${e.congrats_count}` : ""}`
                        : congratsBusy[e.share_id]
                          ? "Sending…"
                          : `🎉 Congratulate${e.congrats_count > 0 ? ` · ${e.congrats_count}` : ""}`}
                    </button>
                  )}
                </div>
              )}
              <div className="flex gap-1.5 flex-wrap mt-2 items-center">
                {EMOJIS.map((emoji) => {
                  const r = e.reactions?.[emoji] || { count: 0, mine: false };
                  return (
                    <button
                      key={emoji}
                      data-testid={`react-${emoji}-${e.event_id}`}
                      onClick={() => react(e, emoji)}
                      className={`px-2 py-1 text-xs inline-flex items-center gap-1 border transition-colors ${
                        r.mine
                          ? "bg-[#D4AF37]/10 border-[#D4AF37] text-[#D4AF37]"
                          : "border-zinc-800 text-neutral-400 hover:border-zinc-600"
                      }`}
                    >
                      <span>{emoji}</span>
                      {r.count > 0 && (
                        <span className="font-bold tabular-nums">{r.count}</span>
                      )}
                    </button>
                  );
                })}
                <button
                  data-testid={`comments-toggle-${e.event_id}`}
                  onClick={() => toggleComments(e.event_id)}
                  className={`px-2 py-1 text-xs inline-flex items-center gap-1 border transition-colors ${
                    openComments[e.event_id]
                      ? "bg-[#D4AF37]/10 border-[#D4AF37] text-[#D4AF37]"
                      : "border-zinc-800 text-neutral-400 hover:border-zinc-600"
                  }`}
                >
                  <MessageCircle size={12} />
                  {commentsByEvent[e.event_id]?.length > 0 && (
                    <span className="font-bold tabular-nums">{commentsByEvent[e.event_id].length}</span>
                  )}
                </button>
              </div>
              {openComments[e.event_id] && (
                <div data-testid={`comments-thread-${e.event_id}`} className="mt-3 pt-3 border-t border-zinc-900 space-y-2">
                  {commentLoading[e.event_id] && (
                    <div className="flex justify-center py-2"><Loader2 className="animate-spin text-neutral-500" size={14} /></div>
                  )}
                  {(commentsByEvent[e.event_id] || []).map((c) => (
                    <div
                      key={c.comment_id}
                      className="flex items-start gap-2 group"
                      data-testid={`comment-${c.comment_id}`}
                    >
                      {c.author_picture ? (
                        <img src={c.author_picture} alt={c.author_name} className="w-7 h-7 object-contain bg-black border border-zinc-700" />
                      ) : (
                        <div className="w-7 h-7 bg-[#4C1D95] border border-[#D4AF37] flex items-center justify-center text-xs font-display text-[#D4AF37] flex-shrink-0">
                          {(c.author_name || "?").charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className="flex-1 min-w-0 bg-[#0A0A0A] border border-zinc-900 p-2">
                        <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-bold flex items-center gap-2">
                          <span>{c.is_mine ? "You" : c.author_name}</span>
                          <span className="text-neutral-600 normal-case tracking-wider">{timeAgo(c.timestamp)}</span>
                        </div>
                        <p className="text-sm text-neutral-200 mt-0.5 break-words">{c.text}</p>
                      </div>
                      {(c.is_mine || e.is_me || user?.role === "owner") && (
                        <button
                          data-testid={`comment-delete-${c.comment_id}`}
                          onClick={() => deleteComment(e.event_id, c.comment_id)}
                          className="text-neutral-700 hover:text-[#DC2626] opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-1"
                          title="Delete"
                        >
                          <Trash2 size={12} />
                        </button>
                      )}
                    </div>
                  ))}
                  {(commentsByEvent[e.event_id] || []).length === 0 && !commentLoading[e.event_id] && (
                    <p className="text-xs text-neutral-600 text-center py-1">Be the first to comment.</p>
                  )}
                  <div className="flex items-center gap-2 pt-1">
                    <input
                      data-testid={`comment-input-${e.event_id}`}
                      type="text"
                      value={draftByEvent[e.event_id] || ""}
                      onChange={(ev) => setDraftByEvent((m) => ({ ...m, [e.event_id]: ev.target.value }))}
                      onKeyDown={(ev) => ev.key === "Enter" && postComment(e.event_id)}
                      placeholder="Add a comment... (Enter to send)"
                      maxLength={280}
                      className="input-omni flex-1"
                    />
                    <button
                      data-testid={`comment-send-${e.event_id}`}
                      onClick={() => postComment(e.event_id)}
                      disabled={!draftByEvent[e.event_id]?.trim()}
                      className="w-9 h-9 bg-[#D4AF37] text-black hover:bg-[#F1C84B] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
                      title="Send"
                    >
                      <Send size={14} />
                    </button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
